import './jsonSidePanel.css';
import React, { useState, useEffect, useMemo } from 'react';
import { ChevronsRight as ChevronsRightIcon, Trash2 } from 'lucide-react';
import SQLTable, { TableColumnConfig, JsonColumnConfig } from '../tableMain';
import TableInput from '../../common/table_input/tableInput';
import { useApiData } from '../../../contexts/apiService';
import { CrudeTanksDefinitionItem } from '../../../contexts/tableDefinitions';
import { updateJsonArray, filterArrayToOriginalFields } from '../../../helper_functions';
import { InputColumnConfig } from '../../common/table_input/tableInput';
import { useDataManager } from '../../../contexts/dataManager';

interface JsonSidePanelProps {
    rowData: any;
    tableName: string; // actual sql table name
    idColumn: string;
    columnConfigs: TableColumnConfig;
    jsonColumnConfig?: JsonColumnConfig;
    isOpen: boolean;
    onClose: () => void;
}

const JsonSidePanel: React.FC<JsonSidePanelProps> = ({
    rowData,
    tableName,
    idColumn,
    columnConfigs,
    jsonColumnConfig,
    isOpen,
    onClose
}) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const { apiData } = useApiData();
    const { handleEditOrder } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    
    useEffect(() => {
        // Request data for tables
        if (jsonColumnConfig && jsonColumnConfig.SQLTable && !hasTableData(jsonColumnConfig.SQLTable.tableName)) {
            requestTableData(jsonColumnConfig.SQLTable.tableName);
        }
    }, [jsonColumnConfig?.SQLTable?.tableName]);

    // Helper function to convert JsonColumnConfig to InputColumnConfig array
    const convertToInputConfigs = (jsonConfig: JsonColumnConfig): InputColumnConfig[] => {
        return (jsonConfig.fields || []).map(field => ({
            colName: field.colName,
            colType: field.colType,
            placeholder: field.placeholder || `Enter ${field.colName}`,
            required: field.required,
            inputType: field.inputType,
            dropdownOptions: field.dropdownOptions
        }));
    };
    
    // Get filtered JSON string for the current data
    const getFilteredJsonString = (): string => {
        const originalFields = columnConfigs.jsonCols || [];
        return filterArrayToOriginalFields(rowData[columnConfigs.colName] || '[]', originalFields);
    };

    const renderJsonContent = () => {
        const jsonData = rowData[columnConfigs.colName];
        
        // Helper function for error/empty states
        const renderState = (message: string, isError: boolean = false) => (
            <div className="jsonSidePanel-dataContainer">
                <span className={isError ? "jsonSidePanel-error" : "jsonSidePanel-empty"}>{message}</span>
            </div>
        );

        // Handle null/undefined
        if (jsonData === null || jsonData === undefined) {
            return renderState("No data");
        }

        try {
            const jsonArray = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
            
            // Handle non-array or empty array
            if (!Array.isArray(jsonArray)) return renderState("Data is not an array", true);
            if (jsonArray.length === 0) return renderState("No items in array");

            // Get display columns - use displayCols if specified, otherwise get all available columns
            const getDisplayColumns = (): string[] => {
                // If displayCols is specified in jsonColumnConfig, use it
                if (jsonColumnConfig?.displayJsonCols && jsonColumnConfig.displayJsonCols.length > 0) {
                    return jsonColumnConfig.displayJsonCols.map(col => col.colName);
                }
                
                // Otherwise, get all unique keys from all objects in the array
                const allKeys = new Set<string>();
                jsonArray.forEach(item => {
                    if (item && typeof item === 'object') {
                        Object.keys(item).forEach(key => allKeys.add(key));
                    }
                });
                
                return Array.from(allKeys);
            };

            const displayColumns = getDisplayColumns();

            // Render items
            return (
                <div className="jsonSidePanel-dataContainer">
                    <div className="jsonSidePanel-itemList">
                        {jsonArray.map((item, index) => (
                            <div 
                                key={index} 
                                className="jsonSidePanel-rowItem"
                            >
                                <div className="json-item-content">
                                    {displayColumns.map((columnKey) => (
                                        <div key={columnKey} className="json-item-field">
                                            <span className="json-field-value">
                                                {item[columnKey] !== undefined && item[columnKey] !== null 
                                                    ? String(item[columnKey]) 
                                                    : '-'
                                                }
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <button 
                                    className="json-item-delete"
                                    onClick={() => handleRemoveItem(item)}
                                    title="Remove item"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            );
        } catch (error) {
            return renderState("Invalid JSON format", true);
        }
    };

    // Function to handle removing an item from the JSON array
    const handleRemoveItem = async (itemToRemove: any) => {
        try {

            if (jsonColumnConfig) {
                const updatedJsonString = updateJsonArray(rowData[columnConfigs.colName], 'remove', itemToRemove);
                
                // Filter itemToRemove to only include original fields
                const originalFields = columnConfigs.jsonCols || [];
                const oldFilteredJsonString = getFilteredJsonString();
                const newFilteredJsonString = filterArrayToOriginalFields(updatedJsonString, originalFields);
                await handleEditOrder(tableName, rowData[idColumn], jsonColumnConfig.pointsTo, oldFilteredJsonString, newFilteredJsonString);
            }

        } catch (error) {
            console.error('Error removing item:', error);
        }
    };

    // key listener for closing on Escape key press
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        // Add event listener when panel is open
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        // Cleanup function to remove event listener
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    // give the panel time before closing so it can animate before its removed from the DOM
    const handleClose = () => {
        setIsAnimating(true);
        // Wait for animation to complete before actually closing
        setTimeout(() => {
            onClose();
            setIsAnimating(false);
        }, 150); // Match the CSS animation duration
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        // Close panel when clicking on the overlay (outside the panel)
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!isOpen) return null;
    return (
        <div className="jsonSidePanel-overlay" onClick={handleOverlayClick}>
            <div className={`jsonSidePanel-mainContainer ${isAnimating ? 'closing' : ''}`}>
                <div className="jsonSidePanel-header">
                    <button
                        className="jsonSidePanel-closeButton"
                        onClick={handleClose}
                    >
                        <ChevronsRightIcon 
                            size={20} 
                            style={{color: 'var(--color-grey-light-text)'}}
                        />
                    </button>
                    <div className='jsonSidePanel-headerText'>
                        {jsonColumnConfig?.panelHeader ? jsonColumnConfig.panelHeader(rowData) : 'Details'}
                    </div>
                </div>
                <div className="jsonSidePanel-inputContainer">
                    {jsonColumnConfig && (
                        (jsonColumnConfig.SQLTable && jsonColumnConfig.tableInput) ? (
                            <TableInput
                                tableName={jsonColumnConfig.SQLTable.tableName}
                                inputColumnConfigs={jsonColumnConfig.tableInput}
                                parentId={rowData[idColumn]}
                                addMultipleRows={false}
                            />
                        ) : (
                            <TableInput
                                tableName={tableName}
                                inputColumnConfigs={convertToInputConfigs(jsonColumnConfig)}
                                arrangement='column'
                                tableType={'json'}
                                jsonColName={jsonColumnConfig.pointsTo}
                                jsonColValue={getFilteredJsonString()}
                                rowIDValue={rowData[idColumn]}
                                addMultipleRows={false}
                                clearButton={false}
                            />
                        )
                    )}
                </div>
                <div className="jsonSidePanel-dataContainer">
                    <div className="jsonSidePanel-itemList">
                        {jsonColumnConfig && (
                            jsonColumnConfig.SQLTable ? (
                                <SQLTable
                                    tableName={jsonColumnConfig.SQLTable.tableName}
                                    tableData={(apiData?.[jsonColumnConfig.SQLTable.tableName as keyof typeof apiData] ?? []) as Record<string, any>[]}
                                    idColumn={jsonColumnConfig.SQLTable.idColumn}
                                    preFilterData={{ column: jsonColumnConfig.SQLTable.preFilterData?.column || '', value: rowData[idColumn] }}
                                    columnConfigs={jsonColumnConfig.SQLTable.columnConfigs}
                                    showHeader={true}
                                    selectableRows={true}
                                    multipleSelection={true}
                                    deletableRows={true}
                                    enableFilters={true}
                                    showTable={true}
                                />
                            ) : (
                                renderJsonContent()
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JsonSidePanel;